// src/app/services/auth.service.ts
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';


interface AuthResponse {
  token: string;
  user: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly TOKEN_EXPIRY_KEY = 'auth_token_expiry';
  private apiUrl = `${environment.apiUrl}/auth`;
  
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  
  // Observable für den Auth-Status
  private authStateSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
  public authState$ = this.authStateSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Prüfe Token-Gültigkeit beim Start
    if (this.isBrowser) {
      this.checkTokenExpiration();
    }
  }

  login(email: string, password: string, rememberMe: boolean): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, { email, password })
      .pipe(
        tap(response => {
          if (this.isBrowser) {
            this.storeAuthData(response.token, response.user, rememberMe);
            this.authStateSubject.next(true);
          }
        }),
        catchError(error => {
          console.error('Login error:', error);
          return throwError(() => new Error(
            error.error?.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.'
          ));
        })
      );
  }

  logout(): void {
    if (this.isBrowser) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.USER_KEY);
      sessionStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      this.authStateSubject.next(false);
    }
    this.router.navigate(['/auth']);
  }

  getToken(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    
    // Prüfe, ob Token abgelaufen ist
    if (this.isTokenExpired()) {
      this.logout();
      return null;
    }
    
    return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    if (!this.isBrowser) {
      return false; // Beim Server-Rendering immer false
    }
    return !!this.getToken();
  }

  getCurrentUser(): User | null {
    if (!this.isBrowser) {
      return null;
    }
    
    const userStr = localStorage.getItem(this.USER_KEY) || sessionStorage.getItem(this.USER_KEY);
    if (!userStr) {
      return null;
    }
    
    try {
      return JSON.parse(userStr);
    } catch (e) {
      console.error('Error parsing user data', e);
      return null;
    }
  }

  private storeAuthData(token: string, user: any, rememberMe: boolean): void {
    if (!this.isBrowser) {
      return;
    }
    
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Token speichern
    storage.setItem(this.TOKEN_KEY, token);
    storage.setItem(this.USER_KEY, JSON.stringify(user));
    
    // Ablaufzeit berechnen und speichern (falls JWT)
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        if (payload.exp) {
          storage.setItem(this.TOKEN_EXPIRY_KEY, payload.exp.toString());
        }
      }
    } catch (e) {
      console.warn('Token konnte nicht dekodiert werden', e);
    }
  }

  private isTokenExpired(): boolean {
    if (!this.isBrowser) {
      return true;
    }
    
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY) || sessionStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return false; // Keine Ablaufzeit gefunden, Annahme: Token gültig
    }
    
    const expiryTimestamp = parseInt(expiry, 10) * 1000; // In Millisekunden
    return Date.now() >= expiryTimestamp;
  }

  private checkTokenExpiration(): void {
    if (this.isTokenExpired()) {
      this.logout();
    }
  }
}